/**
 * AdaptiveTimeSeriesChart — automatically selects the best chart type
 * based on data density.
 *
 * - sparse (<2/day): weekly bars or period comparison
 * - medium (2–10/day): cumulative line
 * - dense (>10/day): original line/area chart
 *
 * Users can override the auto-selection via a toggle.
 */

import { useState, useMemo } from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { ChartBarIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import type { ChartDataPoint } from '@/services/analytics.service';
import {
  computeDataDensity,
  getRecommendedChartType,
  aggregateToWeekly,
  toCumulativeLine,
  toPeriodComparison,
  type RecommendedChartType,
} from '@/utils/chart-adaptation';
import WeeklyBarChart from './WeeklyBarChart';
import PeriodComparisonBar from './PeriodComparisonBar';

interface AdaptiveTimeSeriesChartProps {
  data: ChartDataPoint[];
  color: string;
  gradientId: string;
  height?: number;
  originalType?: 'line' | 'area';
  /** Force a specific chart type, bypassing auto-adaptation */
  forceType?: RecommendedChartType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderTooltip?: (props: any) => React.ReactNode;
}

const CHART_TYPE_LABELS: Record<RecommendedChartType, string> = {
  weeklyBar: 'Weekly',
  periodComparison: 'Period Comparison',
  cumulativeLine: 'Cumulative',
  originalLine: 'Daily',
  originalArea: 'Daily',
};

const AdaptiveTimeSeriesChart: React.FC<AdaptiveTimeSeriesChartProps> = ({
  data,
  color,
  gradientId,
  height = 300,
  originalType = 'area',
  forceType,
  renderTooltip,
}) => {
  const [showOriginal, setShowOriginal] = useState(false);

  const { density } = useMemo(() => computeDataDensity(data), [data]);
  const recommended = useMemo(
    () => forceType ?? getRecommendedChartType(data, originalType),
    [data, originalType, forceType]
  );

  const activeType: RecommendedChartType = showOriginal
    ? originalType === 'line'
      ? 'originalLine'
      : 'originalArea'
    : recommended;

  const weeklyData = useMemo(() => aggregateToWeekly(data), [data]);
  const cumulativeData = useMemo(() => toCumulativeLine(data), [data]);
  const periodData = useMemo(() => toPeriodComparison(data), [data]);

  const isAdapted = activeType !== 'originalLine' && activeType !== 'originalArea';

  // Density badge
  const densityBadge = (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        density === 'sparse'
          ? 'bg-warning-100 text-warning-700 dark:bg-warning-900/20 dark:text-warning-400'
          : density === 'medium'
            ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
            : 'bg-success-100 text-success-700 dark:bg-success-900/20 dark:text-success-400'
      }`}
    >
      {CHART_TYPE_LABELS[activeType]}
    </span>
  );

  return (
    <div className="space-y-2">
      {/* Controls row */}
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center space-x-2">
          <ChartBarIcon className="w-3.5 h-3.5 text-secondary-400" />
          {densityBadge}
        </div>
        {isAdapted && (
          <button
            onClick={() => setShowOriginal(!showOriginal)}
            className="flex items-center space-x-1 text-xs text-secondary-500 hover:text-secondary-700 dark:text-secondary-400 dark:hover:text-secondary-200 transition-colors"
          >
            <ArrowsRightLeftIcon className="w-3.5 h-3.5" />
            <span>{showOriginal ? 'Auto' : 'Daily view'}</span>
          </button>
        )}
        {!isAdapted && density !== 'dense' && recommended !== activeType && (
          <button
            onClick={() => setShowOriginal(false)}
            className="flex items-center space-x-1 text-xs text-secondary-500 hover:text-secondary-700 dark:text-secondary-400 dark:hover:text-secondary-200 transition-colors"
          >
            <ArrowsRightLeftIcon className="w-3.5 h-3.5" />
            <span>Auto</span>
          </button>
        )}
      </div>

      {/* Chart */}
      {activeType === 'weeklyBar' && (
        <WeeklyBarChart
          data={weeklyData}
          color={color}
          gradientId={`${gradientId}-weekly`}
          height={height}
          renderTooltip={renderTooltip}
        />
      )}

      {activeType === 'periodComparison' && (
        <PeriodComparisonBar data={periodData} color={color} height={height} />
      )}

      {activeType === 'cumulativeLine' && (
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart data={cumulativeData}>
            <defs>
              <linearGradient id={`${gradientId}-cumulative`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.4} />
                <stop offset="95%" stopColor={color} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.5} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12, fill: '#64748b' }}
              tickLine={{ stroke: '#cbd5e1' }}
              axisLine={{ stroke: '#cbd5e1' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#64748b' }}
              tickLine={{ stroke: '#cbd5e1' }}
              axisLine={{ stroke: '#cbd5e1' }}
            />
            {renderTooltip ? (
              <Tooltip content={renderTooltip} />
            ) : (
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                }}
              />
            )}
            <Area
              type="monotone"
              dataKey="value"
              name="Cumulative"
              stroke={color}
              strokeWidth={3}
              fill={`url(#${gradientId}-cumulative)`}
              animationDuration={300}
              animationEasing="ease-out"
            />
          </AreaChart>
        </ResponsiveContainer>
      )}

      {activeType === 'originalArea' && (
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart data={data}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.4} />
                <stop offset="95%" stopColor={color} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.5} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12, fill: '#64748b' }}
              tickLine={{ stroke: '#cbd5e1' }}
              axisLine={{ stroke: '#cbd5e1' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#64748b' }}
              tickLine={{ stroke: '#cbd5e1' }}
              axisLine={{ stroke: '#cbd5e1' }}
            />
            {renderTooltip ? (
              <Tooltip content={renderTooltip} />
            ) : (
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                }}
              />
            )}
            <Area
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={3}
              fill={`url(#${gradientId})`}
              animationDuration={300}
              animationEasing="ease-out"
            />
          </AreaChart>
        </ResponsiveContainer>
      )}

      {activeType === 'originalLine' && (
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.5} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12, fill: '#64748b' }}
              tickLine={{ stroke: '#cbd5e1' }}
              axisLine={{ stroke: '#cbd5e1' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#64748b' }}
              tickLine={{ stroke: '#cbd5e1' }}
              axisLine={{ stroke: '#cbd5e1' }}
            />
            {renderTooltip ? (
              <Tooltip content={renderTooltip} />
            ) : (
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                }}
              />
            )}
            <Line
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={3}
              dot={{ fill: '#fff', stroke: color, strokeWidth: 3, r: 5 }}
              activeDot={{ r: 7, fill: color }}
              animationDuration={300}
              animationEasing="ease-out"
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default AdaptiveTimeSeriesChart;
